import React, { useState, useEffect, useRef } from 'react';

import classNames from 'classnames';
import { Field } from 'react-final-form';

import { RangeSlider } from '../../../components';
import { decimalPlacesOf, isValidRangeValue, parseRangeValue } from '../../../util/numberRange';

import css from './IntegerRangeFilter.module.css';

//  Ensures that the given values object has both minimum and maximum values.
//  If `values` does not include `minValue` or `maxValue`, defaults are applied.
const resolveMinMaxValues = (values, defaultMax, defaultMin) => {
  const { maxValue, minValue } = values || {};
  return {
    maxValue: maxValue != null ? maxValue : defaultMax,
    minValue: minValue != null ? minValue : defaultMin,
  };
};

const isTooSmall = (x, limit) => x < limit;
const isTooBig = (x, limit) => x > limit;
// `decimals` comes from the filter's step. It is 0 for every whole-number filter,
// including Price, and those take exactly the same code path as before.
const isValidMin = (minValue, minLimit, maxValue, decimals = 0) => {
  return (
    isValidRangeValue(minValue, decimals) &&
    !isTooSmall(minValue, minLimit) &&
    !isTooBig(minValue, maxValue)
  );
};
const isValidMax = (maxValue, maxLimit, minValue, decimals = 0) => {
  return (
    isValidRangeValue(maxValue, decimals) &&
    !isTooBig(maxValue, maxLimit) &&
    !isTooSmall(maxValue, minValue)
  );
};

const getValidHandles = (currentValues, inputValues, minLimit, maxLimit, decimals = 0) => {
  const isMinUpdated = currentValues.minValue !== inputValues.minValue;
  const isMaxUpdated = currentValues.maxValue !== inputValues.maxValue;

  if (isMinUpdated && isMaxUpdated) {
    const { minValue, maxValue } = inputValues;
    const inRange = !(isTooSmall(minValue, minLimit) || isTooBig(maxValue, maxLimit));
    // If both values are changed and are within bounds, return the new values. Otherwise, return the default values.
    return inRange && minValue <= maxValue ? [minValue, maxValue] : [minLimit, maxLimit];
  }

  const { minValue, maxValue } = inputValues;
  const validMin = !isMinUpdated
    ? currentValues.minValue
    : !isValidRangeValue(minValue, decimals)
    ? minLimit
    : isTooSmall(minValue, minLimit)
    ? minLimit
    : isTooBig(minValue, maxValue)
    ? maxValue
    : minValue;
  const validMax = !isMaxUpdated
    ? currentValues.maxValue
    : !isValidRangeValue(maxValue, decimals)
    ? maxLimit
    : isTooBig(maxValue, maxLimit)
    ? maxLimit
    : isTooSmall(maxValue, minValue)
    ? minValue
    : maxValue;

  return [validMin, validMax];
};

const RangeInput = props => {
  const {
    input,
    min: defaultMinValue,
    max: defaultMaxValue,
    step,
    isInSideBar,
    initialValues,
    intl,
    getLabelForRangeInput,
  } = props;
  const { value: values = {}, onChange, name } = input;

  // How many decimal places the buyer may type, taken from the step. A whole-number step
  // gives 0, which is how every filter behaved before quarter-carat steps were added.
  const decimals = decimalPlacesOf(step);

  const currentValues = resolveMinMaxValues(initialValues[name], defaultMaxValue, defaultMinValue);
  const [fieldValues, setFieldValues] = useState(currentValues);

  // True while the user has one of the number inputs focused. We must not overwrite
  // a half-typed value with the value that is currently in the URL.
  const isEditingRef = useRef(false);

  // Sync the inputs when the applied range actually changes (e.g. the filter is
  // cleared, or the buyer navigates back).
  //
  // Note: the dependency list holds the min/max numbers, not `initialValues`. The
  // parent rebuilds `initialValues` into a brand-new object on every render, so
  // depending on the object made this effect run constantly and overwrite whatever
  // the buyer was typing.
  useEffect(() => {
    if (isEditingRef.current) {
      return;
    }
    setFieldValues({ minValue: currentValues.minValue, maxValue: currentValues.maxValue });
  }, [currentValues.minValue, currentValues.maxValue]);

  const handleMinValueChange = event => {
    const newValue = parseRangeValue(event.target.value, decimals);

    // Faulty mode: new min value is not a usable number
    if (!isValidRangeValue(newValue, decimals)) {
      const newValues = { ...fieldValues, minValue: '' };
      setFieldValues(newValues);
      return;
    }

    // Faulty mode: new min value is greater than the current max value or
    //              new min value is less than the default min value
    if (newValue > fieldValues.maxValue || newValue < defaultMinValue) {
      const newValues = { ...fieldValues, minValue: newValue };
      setFieldValues(newValues);
      return;
    }

    const newValues =
      newValue < defaultMinValue
        ? { ...fieldValues, minValue: defaultMinValue }
        : { ...fieldValues, minValue: newValue };
    setFieldValues(newValues);
    onChange(newValues);
  };

  const handleMaxValueChange = event => {
    const newValue = parseRangeValue(event.target.value, decimals);

    // Faulty mode: new max value is not a usable number
    if (!isValidRangeValue(newValue, decimals)) {
      const newValues = { ...fieldValues, maxValue: '' };
      setFieldValues(newValues);
      return;
    }

    // Faulty mode: new max value is less than the current min value or
    //              new max value is greater than the default max value
    if (newValue < fieldValues.minValue || newValue > defaultMaxValue) {
      const newValues = { ...fieldValues, maxValue: newValue };
      setFieldValues(newValues);
      return;
    }

    const newValues =
      newValue > defaultMaxValue
        ? { ...fieldValues, maxValue: defaultMaxValue }
        : { ...fieldValues, maxValue: newValue };
    setFieldValues(newValues);
    onChange(newValues);
  };

  const handleFocus = () => {
    isEditingRef.current = true;
  };

  // Blur handlers only repair what is *shown*. They never call onChange, so leaving a
  // box empty or out of range keeps the range that is already applied to the search.
  //
  // Previously these snapped the box to the floor/ceiling of the whole allowed range
  // and submitted it. On a $0-$50,000 price filter that reads as "the filter reset
  // itself" the moment the buyer backspaces a value.
  const handleMinValueBlur = event => {
    isEditingRef.current = false;
    const inputValue = parseRangeValue(event.target.value, decimals);
    const upperBound = isValidRangeValue(fieldValues.maxValue, decimals)
      ? fieldValues.maxValue
      : defaultMaxValue;

    if (isValidMin(inputValue, defaultMinValue, upperBound, decimals)) {
      return;
    }

    // Restore the applied minimum, or the range floor when no range is applied yet.
    const restored = isValidMin(values.minValue, defaultMinValue, upperBound, decimals)
      ? values.minValue
      : currentValues.minValue;
    setFieldValues({ ...fieldValues, minValue: restored });
  };
  const handleMaxValueBlur = event => {
    isEditingRef.current = false;
    const inputValue = parseRangeValue(event.target.value, decimals);
    const lowerBound = isValidRangeValue(fieldValues.minValue, decimals)
      ? fieldValues.minValue
      : defaultMinValue;

    if (isValidMax(inputValue, defaultMaxValue, lowerBound, decimals)) {
      return;
    }

    // Restore the applied maximum, or the range ceiling when no range is applied yet.
    const restored = isValidMax(values.maxValue, defaultMaxValue, lowerBound, decimals)
      ? values.maxValue
      : currentValues.maxValue;
    setFieldValues({ ...fieldValues, maxValue: restored });
  };

  const handleSliderChange = updatedValue => {
    setFieldValues({ ...updatedValue });
    onChange({ ...updatedValue });
  };

  const labelForRangeInput = getLabelForRangeInput
    ? getLabelForRangeInput
    : (value, handleName) =>
        intl.formatMessage(
          { id: 'IntegerRangeFilter.screenreader.rangeHandle' },
          { value, handle: handleName }
        );

  const getHandleLabels = handles => {
    const multipleHandles = handles.length > 1;
    return handles.map((h, idx) => {
      const handleName =
        multipleHandles && idx === 0
          ? 'min'
          : multipleHandles && idx === handles.length - 1
          ? 'max'
          : 'handle';
      return labelForRangeInput(h, handleName);
    });
  };

  const isMinInvalid = !isValidMin(
    fieldValues.minValue,
    defaultMinValue,
    fieldValues.maxValue,
    decimals
  );
  const isMaxInvalid = !isValidMax(
    fieldValues.maxValue,
    defaultMaxValue,
    fieldValues.minValue,
    decimals
  );
  const classes = isInSideBar ? css.formWrapper : null;

  const validHandles = getValidHandles(
    values,
    fieldValues,
    defaultMinValue,
    defaultMaxValue,
    decimals
  );

  return (
    <div className={classes}>
      <div className={classNames(css.contentWrapper, { [css.contentWrapperSidebar]: isInSideBar })}>
        <div className={css.inputsWrapper}>
          {!isInSideBar ? (
            <span className={css.labelPopup}>
              {intl.formatMessage({ id: 'IntegerRangeFilter.rangeInputsLabel' })}
            </span>
          ) : null}
          <input
            className={classNames(css.minValue, {
              [css.valueInSidebar]: isInSideBar,
              [css.invalidInput]: isMinInvalid,
            })}
            type="number"
            name={`${name}_min`}
            min={defaultMinValue}
            max={defaultMaxValue}
            step={step}
            placeholder={defaultMinValue}
            value={fieldValues.minValue}
            onChange={handleMinValueChange}
            onFocus={handleFocus}
            onBlur={handleMinValueBlur}
            aria-valuenow={fieldValues.minValue}
            aria-valuetext={labelForRangeInput(fieldValues.minValue, 'min')}
            aria-valuemin={defaultMinValue}
            aria-valuemax={defaultMaxValue}
            aria-label={labelForRangeInput(fieldValues.minValue, 'min')}
          ></input>
          <span className={css.valueSeparator}>-</span>
          <input
            className={classNames(css.maxValue, {
              [css.valueInSidebar]: isInSideBar,
              [css.invalidInput]: isMaxInvalid,
            })}
            type="number"
            name={`${name}_max`}
            min={defaultMinValue}
            max={defaultMaxValue}
            placeholder={defaultMaxValue}
            step={step}
            value={fieldValues.maxValue}
            onChange={handleMaxValueChange}
            onFocus={handleFocus}
            onBlur={handleMaxValueBlur}
            aria-valuenow={fieldValues.maxValue}
            aria-valuetext={labelForRangeInput(fieldValues.maxValue, 'max')}
            aria-valuemin={defaultMinValue}
            aria-valuemax={defaultMaxValue}
            aria-label={labelForRangeInput(fieldValues.maxValue, 'max')}
          ></input>
        </div>
      </div>
      <div className={css.sliderWrapper}>
        <RangeSlider
          min={defaultMinValue}
          max={defaultMaxValue}
          ariaLabels={getHandleLabels(validHandles)}
          step={step}
          handles={validHandles}
          onChange={handles => {
            handleSliderChange({ minValue: handles[0], maxValue: handles[1] });
          }}
        />
      </div>
    </div>
  );
};

/**
 * FieldSelectIntegerRange component
 *
 * @component
 * @param {Object} props
 * @param {number} [props.min] - The minimum value
 * @param {number} [props.max] - The maximum value
 * @param {string} [props.name] - The name
 * @param {number} [props.step] - The step
 * @param {boolean} [props.isInSideBar] - Whether the filter is in the sidebar
 * @returns {JSX.Element}
 */
const FieldSelectIntegerRange = props => {
  const { max, min, name, step = 5, isInSideBar = false, ...rest } = props;
  return (
    <Field
      max={max}
      min={min}
      name={name}
      step={step}
      isInSideBar={isInSideBar}
      component={RangeInput}
      {...rest}
    />
  );
};

export default FieldSelectIntegerRange;
